// Stok Sayım listesi (/stok/sayim): özet kutuları, filtreler, sayımlar; Hızlı Sayım ve Yeni Sayım.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, InputAdornment, MenuItem, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AddIcon from '@mui/icons-material/Add';
import BoltIcon from '@mui/icons-material/Bolt';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../contexts/AuthContext';
import { WarehouseService, type InventoryWarehouseDto } from '../services/warehouseService';
import {
  fmtDate, fmtMoney, STATUS_COLOR, STATUS_LABEL, stockCountService, TYPE_LABEL,
  type StockCountStatus, type StockCountSummary,
} from './stockCountService';
import NewStockCountDialog from './NewStockCountDialog';
import QuickCountDialog from './QuickCountDialog';
import { FULL_STOCK_COUNT_PERMISSIONS, type StockCountUiPermissions } from './stockCountPermissions';

interface Props {
  /** Verilmezse hepsi açık (admin). Yönetici panelinde alt yetkiler gelir. */
  perms?: StockCountUiPermissions;
  /** Yönetici panelinde yalnız şube depoları. */
  warehouseFilter?: (w: InventoryWarehouseDto) => boolean;
  /** Verilirse sayım sayfaya gitmek yerine bu fonksiyonla açılır. */
  onOpenCount?: (id: string) => void;
}

const StockCountsPage: React.FC<Props> = ({ perms = FULL_STOCK_COUNT_PERMISSIONS, warehouseFilter, onOpenCount }) => {
  const navigate = useNavigate();
  const openCount = (id: string) => (onOpenCount ? onOpenCount(id) : navigate(`/stok/sayim/${id}`));
  const { i18n } = useTranslation();
  const tt = (tr: string, en: string) => ((i18n.language || '').startsWith('en') ? en : tr);
  const { userDetails } = useAuth() as any;
  const companyId: string = userDetails?.companyId || '';

  const [items, setItems] = useState<StockCountSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [warehouses, setWarehouses] = useState<InventoryWarehouseDto[]>([]);
  const [newOpen, setNewOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await stockCountService.list({
        status: status || undefined,
        warehouseId: warehouseId || undefined,
        search: search.trim() || undefined,
        pageSize: 200,
      });
      setItems(res.items);
    } catch (e: any) {
      setError(e?.message || tt('Sayımlar yüklenemedi.', 'Counts could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [status, warehouseId, search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    if (!companyId) return;
    WarehouseService.getAllWarehouses(companyId)
      .then((list) => setWarehouses(list.filter((w) => (w.warehouseType || 'Normal') !== 'Smart' && (!warehouseFilter || warehouseFilter(w)))))
      .catch(() => setWarehouses([]));
  }, [companyId, warehouseFilter]);

  const kpi = useMemo(() => {
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    const doneThisMonth = items.filter((c) => c.status === 'COMPLETED' && c.approvedAt && new Date(`${c.approvedAt}Z`) >= monthStart);
    return {
      open: items.filter((c) => c.status === 'DRAFT' || c.status === 'COUNTING').length,
      review: items.filter((c) => c.status === 'REVIEW').length,
      done: doneThisMonth.length,
      net: doneThisMonth.reduce((sum, c) => sum + (c.surplusValue || 0) + (c.shortageValue || 0), 0),
    };
  }, [items]);

  const progress = (c: StockCountSummary) =>
    c.status === 'DRAFT' ? '—' : `${c.countedLineCount} / ${c.lineCount}`;
  const scopeLabel = (c: StockCountSummary) => TYPE_LABEL[c.type] ?? c.type;
  const net = (c: StockCountSummary) => (c.status === 'COMPLETED' ? (c.surplusValue || 0) + (c.shortageValue || 0) : null);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, display: 'grid', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <FactCheckIcon color="primary" sx={{ fontSize: 34 }} />
        <Typography variant="h4" fontWeight={700} sx={{ flex: 1, minWidth: 200 }}>
          {tt('Stok Sayım', 'Stock Counts')}
        </Typography>
        {!onOpenCount && (
          <Button variant="outlined" onClick={() => navigate('/stok/sayim-farklari')}>
            {tt('Sayım farkları', 'Count differences')}
          </Button>
        )}
        {perms.canQuick && (
          <Button variant="contained" color="success" startIcon={<BoltIcon />} onClick={() => setQuickOpen(true)}>
            {tt('Hızlı Sayım', 'Quick Count')}
          </Button>
        )}
        {perms.canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}>
            {tt('Yeni Sayım', 'New Count')}
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
        {[
          { label: tt('Açık sayım', 'Open counts'), value: String(kpi.open), color: undefined },
          { label: tt('Onay bekleyen', 'Awaiting approval'), value: String(kpi.review), color: 'warning.main' },
          { label: tt('Bu ay tamamlanan', 'Completed this month'), value: String(kpi.done), color: 'success.main' },
          { label: tt('Bu ay net fark', 'Net difference this month'), value: fmtMoney(kpi.net), color: kpi.net < 0 ? 'error.main' : 'success.main' },
        ].map((k) => (
          <Card key={k.label} variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="body2" color="text.secondary">{k.label}</Typography>
              <Typography variant="h5" fontWeight={700} color={k.color}>{k.value}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Paper variant="outlined" sx={{ p: 2, display: 'grid', gap: 1.5 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 1.5 }}>
          <TextField
            size="small"
            placeholder={tt('Sayım no, ad veya depo ara…', 'Search number, name or warehouse…')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField select size="small" label={tt('Durum', 'Status')} value={status} onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="">{tt('Tümü', 'All')}</MenuItem>
            {(Object.keys(STATUS_LABEL) as StockCountStatus[]).map((s) => (
              <MenuItem key={s} value={s}>{STATUS_LABEL[s]}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label={tt('Depo', 'Warehouse')} value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <MenuItem value="">{tt('Tümü', 'All')}</MenuItem>
            {warehouses.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
          </TextField>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 700 } }}>
                <TableCell>{tt('Sayım', 'Count')}</TableCell>
                <TableCell>{tt('Depo', 'Warehouse')}</TableCell>
                <TableCell>{tt('Tür', 'Type')}</TableCell>
                <TableCell>{tt('Sayanlar', 'Counters')}</TableCell>
                <TableCell>{tt('Planlanan', 'Planned')}</TableCell>
                <TableCell align="right">{tt('İlerleme', 'Progress')}</TableCell>
                <TableCell>{tt('Durum', 'Status')}</TableCell>
                <TableCell align="right">{tt('Fark tutarı', 'Difference')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={22} /></TableCell></TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {tt('Henüz sayım yok. "Yeni Sayım" ile planlayın ya da "Hızlı Sayım" ile hemen sayın.',
                      'No counts yet. Plan one with "New Count" or count now with "Quick Count".')}
                  </TableCell>
                </TableRow>
              )}
              {items.map((c) => {
                const n = net(c);
                return (
                  <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => openCount(c.id)}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{c.number}</Typography>
                      {c.name && <Typography variant="caption" color="text.secondary">{c.name}</Typography>}
                    </TableCell>
                    <TableCell>{c.warehouseName}</TableCell>
                    <TableCell>{scopeLabel(c)}</TableCell>
                    <TableCell>{c.assignees.map((a) => a.userName || a.userUid).join(', ') || '—'}</TableCell>
                    <TableCell>{fmtDate(c.plannedStartAt)}</TableCell>
                    <TableCell align="right">{progress(c)}</TableCell>
                    <TableCell><Chip size="small" label={STATUS_LABEL[c.status]} color={STATUS_COLOR[c.status]} /></TableCell>
                    <TableCell align="right" sx={{ color: n == null ? 'text.secondary' : n < 0 ? 'error.main' : 'success.main', fontWeight: 600 }}>
                      {n == null ? '—' : fmtMoney(n)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <NewStockCountDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onSaved={(id) => { setNewOpen(false); openCount(id); }}
        warehouseFilter={warehouseFilter}
      />
      <QuickCountDialog
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        onStarted={(id) => { setQuickOpen(false); openCount(id); }}
        warehouseFilter={warehouseFilter}
      />
    </Box>
  );
};

export default StockCountsPage;
